// SignIn — the sign-in page (redesign A-02). Sign-in goes through Auth0 only
// (FR-1.1), two ways: "Continue with Google / Microsoft" goes straight to that
// provider (a closed set, `@bilda/app/signInProvider`), or the email path,
// where the optional email typed here only pre-fills Auth0's page
// (`login_hint`). The brand side (fixed ink palette) says what the product
// does; the form side signs in. Also: the failure notice (FR-1.4) and an
// optional way back to the landing (F1).
//
// The email is parsed at this boundary: empty → no hint, an address → the
// hint, anything else → a field message and no navigation.
//
// Pure/props-driven: every action is reported UP. Local state is
// view-ephemeral only (the un-submitted email and whether to show its
// message).

export type { SignInProps };
export { SignIn };

import { type LoginHint, parseLoginHint } from "@bilda/app/loginHint";
import type { SignInErrorKind } from "@bilda/app/signInError";
import {
    type SignInProvider,
    signInProviders,
} from "@bilda/app/signInProvider";
import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { Button } from "../../Button/Button.js";
import { Icon, type IconName } from "../../Icon/Icon.js";
import { PublicBrand } from "../../PublicBrand/PublicBrand.js";
import "./SignIn.css";

type SignInProps = {
    // Hands off to Auth0 (a full-page navigation owned by the shell), with
    // the parsed email when one was typed.
    readonly onSignIn: (loginHint?: LoginHint) => void;
    // Hands off to Auth0, straight to one identity provider.
    readonly onSignInWith: (provider: SignInProvider) => void;
    readonly onBack?: () => void;
    // Why the last attempt ended without a session (FR-1.4): shown as a plain
    // message with a "Try again" action.
    readonly failure?: SignInErrorKind;
    readonly onRetry?: () => void;
};

// The design's neutral glyphs (a-02), not the providers' logos.
const providerIcon: Record<SignInProvider, IconName> = {
    google: "globe",
    microsoft: "workspace",
};

const SignIn = ({
    onSignIn,
    onSignInWith,
    onBack,
    failure,
    onRetry,
}: SignInProps) => {
    const { t } = useLingui();
    const [email, setEmail] = useState("");
    const [checked, setChecked] = useState(false);
    const parsed = parseLoginHint(email);
    const invalid = checked && parsed.type === "invalid";

    const failureMessage = (kind: SignInErrorKind): string => {
        switch (kind) {
            case "cancelled":
                return t`Sign-in was cancelled. You can try again whenever you like.`;
            case "failed":
                return t`Something went wrong while signing you in. Please try again.`;
            case "account_disabled":
                return t`This account has been turned off, so it can't be used to sign in.`;
            default:
                return exhaustSwitchCase(kind);
        }
    };

    const submit = () => {
        switch (parsed.type) {
            case "empty":
                onSignIn();
                return;
            case "email":
                onSignIn(parsed.hint);
                return;
            case "invalid":
                setChecked(true);
                return;
            default:
                exhaustSwitchCase(parsed);
        }
    };

    const providerLabel = (provider: SignInProvider): string => {
        switch (provider) {
            case "google":
                return t`Continue with Google`;
            case "microsoft":
                return t`Continue with Microsoft`;
            default:
                return exhaustSwitchCase(provider);
        }
    };

    const points = [
        t`The assistant reads your sources and proposes material.`,
        t`You put it together, review it and approve it.`,
        t`Export to Torus.`,
    ];

    return (
        // The public pages keep their own (light) look whatever the app theme.
        <div className="bilda-iam-sign-in" data-theme="light">
            <aside className="bilda-iam-sign-in__brand">
                <PublicBrand />
                <div className="bilda-iam-sign-in__pitch">
                    <p className="bilda-iam-sign-in__claim">
                        {t`From your sources to finished teaching material.`}
                    </p>
                    <ul className="bilda-iam-sign-in__points">
                        {points.map((point) => (
                            <li key={point}>
                                <Icon name="check" size="sm" />
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>
            </aside>
            <main className="bilda-iam-sign-in__side">
                <form
                    className="bilda-iam-sign-in__form"
                    noValidate
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}
                >
                    <h1 className="bilda-iam-sign-in__title">
                        <Trans>Sign in</Trans>
                    </h1>
                    <p className="bilda-iam-sign-in__lede">
                        {t`Pick up where you left off. Your library is waiting.`}
                    </p>
                    {failure !== undefined ? (
                        <div
                            role="alert"
                            className="bilda-iam-sign-in__failure"
                        >
                            <span>{failureMessage(failure)}</span>
                            {onRetry ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onRetry}
                                >
                                    <Trans>Try again</Trans>
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    {signInProviders.map((provider) => (
                        <button
                            key={provider}
                            type="button"
                            className="bilda-iam-sign-in__provider"
                            onClick={() => onSignInWith(provider)}
                        >
                            <Icon name={providerIcon[provider]} size="sm" />
                            {providerLabel(provider)}
                        </button>
                    ))}
                    <div className="bilda-iam-sign-in__or">
                        <span>{t`or with email`}</span>
                    </div>
                    <label className="bilda-iam-sign-in__field">
                        <span className="bilda-iam-sign-in__label">
                            {t`Email`}
                        </span>
                        <span
                            className="bilda-iam-sign-in__input"
                            data-invalid={invalid ? "true" : undefined}
                        >
                            <Icon name="person" size="sm" />
                            <input
                                type="email"
                                autoComplete="email"
                                inputMode="email"
                                value={email}
                                placeholder={t`name@school.org`}
                                aria-invalid={invalid}
                                aria-describedby={
                                    invalid
                                        ? "bilda-sign-in-email-error"
                                        : undefined
                                }
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setChecked(false);
                                }}
                            />
                        </span>
                        {invalid ? (
                            <span
                                id="bilda-sign-in-email-error"
                                className="bilda-iam-sign-in__error"
                            >
                                {t`That doesn't look like an email address.`}
                            </span>
                        ) : null}
                    </label>
                    <button type="submit" className="bilda-iam-sign-in__go">
                        <Trans>Continue</Trans>
                    </button>
                    <p className="bilda-iam-sign-in__note">
                        {t`When you sign in, the text of your sources and materials is sent to an AI provider so the assistant can answer.`}
                    </p>
                    {onBack ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            className="bilda-iam-sign-in__back"
                        >
                            <Icon name="chevron-left" size="sm" />
                            <Trans>Back</Trans>
                        </Button>
                    ) : null}
                </form>
            </main>
        </div>
    );
};
